import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DateValue } from '@givebutter/primitives/date-picker';
import { Separator } from '@givebutter/primitives/separator';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { updateCampaign } from 'api/campaigns';
import {
    CampaignSettingsTimezoneSelect,
    TimezoneSelectFormValues,
} from 'assets/js/pages/campaign-settings/campaign-settings/CampaignSettingsTimezoneSelect';
import * as Form from 'components/form';
import * as SettingsCard from 'components/settings-card';
import { CAMPAIGN_TYPES_LINK } from 'constants/links';
import { Settings } from 'constants/settings';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import {
    dateToServerDateTime,
    getClosestThirtyMinuteDateTime,
    isoDateFromServer,
} from 'helpers/dates';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { accountQueryKeys } from 'queries/account-query-keys';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { Campaign } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface GeneralFormValues extends TimezoneSelectFormValues {
    isPublished: boolean;
    campaignType: Campaign['type'];
    showAmountRaised: boolean;
    hasEndDate: boolean;
    endDate: DateValue;
    allowPaymentsAfterEndDate: boolean;
    showSupporterFeed: boolean;
    enableSpaces: boolean;
    showContactInformation: boolean;
    donateButtonText: string;
}

const CampaignGeneralSettings = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { data: campaign } = useCurrentCampaign();
    const campaignId = campaign?.id ?? 0;
    const currentAccount = useCurrentAccount();
    const { auctionOrganizerInfo = false } = useFlags();
    const isPublished = !!campaign?.published;
    const isUnpublishable = !!campaign?.can_unpublish;
    const isPublishable = !!campaign?.can_publish;

    const methods = Form.useGivebutterForm<GeneralFormValues>({
        defaultValues: {
            isPublished: !!campaign?.published,
            campaignType: campaign?.type ?? 'event',
            timezone: campaign?.timezone || currentAccount.timezone,
            hasEndDate: !!campaign?.end_at,
            endDate: [
                campaign?.end_at
                    ? isoDateFromServer(campaign.end_at)
                    : getClosestThirtyMinuteDateTime(),
            ],
            allowPaymentsAfterEndDate: !(campaign?.forceEndDate?.active ?? false),
            showAmountRaised: !(campaign?.hideMoneyRaised?.active ?? false),
            showSupporterFeed: !(campaign?.hideSupporterFeed?.active ?? false),
            enableSpaces: !(campaign?.disableSpaces?.active ?? false),
            showContactInformation: !(campaign?.hideContactInformation?.active ?? false),
            donateButtonText: campaign?.donationButtonText?.value ?? '',
        },
    });
    const { control, path, handleSubmit, watch } = methods;

    const hasEndDate = watch('hasEndDate');

    const { mutate: submit } = useMutation({
        mutationFn: (values: GeneralFormValues) => {
            const endDateTime = Array.isArray(values.endDate) ? values.endDate[0] : values.endDate;

            const data = {
                published: values.isPublished,
                type: values.campaignType,
                timezone: values.timezone,
                endDate: [
                    campaign?.end_at
                        ? isoDateFromServer(campaign.end_at)
                        : getClosestThirtyMinuteDateTime(),
                ],
                end_at: values.hasEndDate && endDateTime ? dateToServerDateTime(endDateTime) : null,
                settings: [
                    {
                        code: Settings.ForceEndDate.Code,
                        active:
                            !values.hasEndDate ||
                            !(values.hasEndDate && values.allowPaymentsAfterEndDate),
                    },
                    {
                        code: Settings.HideMoneyRaised.Code,
                        active: !values.showAmountRaised,
                    },
                    {
                        code: Settings.HideSupporterFeed.Code,
                        active: !values.showSupporterFeed,
                    },
                    ...(auctionOrganizerInfo
                        ? [
                              {
                                  code: Settings.HideContactInformation.Code,
                                  active: !values.showContactInformation,
                              },
                          ]
                        : []),
                    {
                        code: Settings.DisableSpaces.Code,
                        active: !values.enableSpaces,
                    },
                    {
                        code: Settings.DonationButtonText.Code,
                        value: values.donateButtonText,
                        active: values.donateButtonText.length > 0,
                    },
                ],
            };

            return updateCampaign({ campaignId, data });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.campaign(campaignId),
            });

            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.campaigns(campaign.account_id),
            });

            toast.success('Updated general settings!');
        },
        onError: (e: GivebutterAxiosError) =>
            toast.error(e.response?.data?.message ?? 'Failed to update general settings'),
    });

    if (campaign?.type === 'general') return null;

    return (
        <Form.Provider {...methods}>
            <SettingsCard.Root>
                <SettingsCard.LeftSide>
                    <SettingsCard.Heading>General</SettingsCard.Heading>
                    <p className={'text-sm text-gray-700'}>
                        Publish your campaign and manage your general campaign settings.
                    </p>
                </SettingsCard.LeftSide>
                <SettingsCard.RightSide>
                    <form onSubmit={handleSubmit(submit as SubmitHandler<GeneralFormValues>)}>
                        <div className={'flex flex-col gap-4'}>
                            <div className={'flex flex-col gap-2'}>
                                <div className={'flex items-center justify-between gap-2'}>
                                    <Form.Label className={'text-base'} name={path('isPublished')}>
                                        Publish campaign
                                    </Form.Label>
                                    <Form.Switch
                                        disabled={
                                            (isPublished && !isUnpublishable) ||
                                            (!isPublished && !isPublishable)
                                        }
                                        control={control}
                                        name={'isPublished'}
                                    />
                                </div>
                                <div className={'flex flex-col'}>
                                    <p className={'text-sm text-gray-700'}>
                                        Make this campaign publicly viewable and live.
                                    </p>
                                    <p className={'text-xs italic text-gray-700'}>
                                        Note: You cannot un-publish a campaign once you've started
                                        raising money.
                                    </p>
                                </div>
                                {!isPublished && !isPublishable && (
                                    <p className={'text-sm font-medium text-red-500'}>
                                        Please visit the Payouts tab to setup your withdrawal method
                                        before accepting payments and publishing your campaign.
                                    </p>
                                )}
                            </div>
                            <Separator />
                            <Form.Container>
                                <Form.Label className={'text-base'} name={path('campaignType')}>
                                    Campaign type
                                </Form.Label>
                                <Form.SingleSelect control={control} name={'campaignType'}>
                                    <SingleSelect.Trigger aria-label="Campaign type">
                                        <SingleSelect.Value placeholder="Select a campaign type" />
                                    </SingleSelect.Trigger>
                                    <SingleSelect.Content>
                                        <SingleSelect.Item value="collect">
                                            <SingleSelect.ItemText>Form</SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                        <SingleSelect.Item value="fundraise">
                                            <SingleSelect.ItemText>Page</SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                        <SingleSelect.Item value="event">
                                            <SingleSelect.ItemText>Event</SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                    </SingleSelect.Content>
                                </Form.SingleSelect>

                                <p className={'text-sm text-gray-700'}>
                                    Change your campaign type here. Not sure which to pick?{' '}
                                    <a
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        href={CAMPAIGN_TYPES_LINK}
                                    >
                                        Review the different campaign types
                                    </a>
                                    .
                                </p>
                            </Form.Container>
                            <Form.Container>
                                <div>
                                    <Form.Label className={'text-base'} name={path('timezone')}>
                                        Time zone
                                    </Form.Label>
                                    <p className={'text-sm text-gray-600'}>
                                        This will be used for the start and end time of your event.
                                    </p>
                                </div>
                                <CampaignSettingsTimezoneSelect />
                                <Form.UseFormError name={path('timezone')} />
                            </Form.Container>
                            <Separator />
                            {campaign?.type !== 'collect' && (
                                <div className={'flex flex-col gap-4'}>
                                    <Form.Container>
                                        <div className={'flex items-center justify-between'}>
                                            <Form.Label
                                                className={'text-base'}
                                                name={path('hasEndDate')}
                                            >
                                                End date
                                            </Form.Label>
                                            <Form.Switch control={control} name={'hasEndDate'} />
                                        </div>
                                        <p className={'text-sm text-gray-700'}>
                                            Add an end date/countdown to your page.
                                        </p>
                                        {hasEndDate && (
                                            <Form.UseFormDateTimePicker
                                                control={control}
                                                name={'endDate'}
                                                allowRange={false}
                                                startDateTimeProps={{
                                                    startInputProps: {
                                                        placeholder: 'Select date & time',
                                                    },
                                                }}
                                            />
                                        )}
                                    </Form.Container>
                                    <div className={'flex items-center justify-between'}>
                                        <Form.Label
                                            className={'text-base'}
                                            name={path('allowPaymentsAfterEndDate')}
                                        >
                                            Accept payments even after the end date
                                        </Form.Label>
                                        <Form.Switch
                                            control={control}
                                            name={'allowPaymentsAfterEndDate'}
                                        />
                                    </div>
                                    <Separator />
                                    <div className={'flex flex-col gap-2'}>
                                        <div className={'flex items-center justify-between gap-2'}>
                                            <Form.Label
                                                className={'text-base'}
                                                name={path('showAmountRaised')}
                                            >
                                                Money raised
                                            </Form.Label>
                                            <Form.Switch
                                                control={control}
                                                name={'showAmountRaised'}
                                            />
                                        </div>
                                        <div className={'flex flex-col'}>
                                            <p className={'text-sm text-gray-700'}>
                                                Display the amount of money raised in your campaign
                                                publicly.
                                            </p>
                                        </div>
                                    </div>
                                    <div className={'flex flex-col gap-2'}>
                                        <div className={'flex items-center justify-between gap-2'}>
                                            <Form.Label
                                                className={'text-base'}
                                                name={path('showSupporterFeed')}
                                            >
                                                Supporter feed
                                            </Form.Label>
                                            <Form.Switch
                                                control={control}
                                                name={'showSupporterFeed'}
                                            />
                                        </div>
                                        <div className={'flex flex-col'}>
                                            <p className={'text-sm text-gray-700'}>
                                                Display the supporter feed on your campaign
                                                publicly.
                                            </p>
                                        </div>
                                    </div>
                                    <div className={'flex flex-col gap-2'}>
                                        <div className={'flex items-center justify-between gap-2'}>
                                            <Form.Label
                                                className={'text-base'}
                                                name={path('enableSpaces')}
                                            >
                                                Public messages
                                            </Form.Label>
                                            <Form.Switch control={control} name={'enableSpaces'} />
                                        </div>
                                        <div className={'flex flex-col'}>
                                            <p className={'text-sm text-gray-700'}>
                                                Allow supporters to leave a public message on your
                                                supporter feed.
                                            </p>
                                        </div>
                                    </div>
                                    {auctionOrganizerInfo && (
                                        <div className={'flex flex-col gap-2'}>
                                            <div
                                                className={
                                                    'flex items-center justify-between gap-2'
                                                }
                                            >
                                                <Form.Label
                                                    className={'text-base'}
                                                    name={path('showContactInformation')}
                                                >
                                                    Email, phone, and address
                                                </Form.Label>
                                                <Form.Switch
                                                    control={control}
                                                    name={'showContactInformation'}
                                                />
                                            </div>
                                            <div className={'flex flex-col'}>
                                                <p className={'text-sm text-gray-700'}>
                                                    Display your organization's email, phone, and
                                                    address on your campaign page and receipts.
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    <Separator />
                                    <Form.Container>
                                        <Form.Label
                                            className={'text-base'}
                                            name={path('donateButtonText')}
                                        >
                                            Custom donate button text
                                        </Form.Label>
                                        <p className={'text-sm text-gray-700'}>
                                            Change the text of the main button on your campaign
                                            page.
                                        </p>
                                        <Form.TextInputRoot
                                            control={control}
                                            name={'donateButtonText'}
                                        >
                                            <Form.TextInputForm placeholder={'Donate'} />
                                        </Form.TextInputRoot>
                                    </Form.Container>
                                </div>
                            )}
                            <div>
                                <Form.SubmitButton>Save</Form.SubmitButton>
                            </div>
                        </div>
                    </form>
                </SettingsCard.RightSide>
            </SettingsCard.Root>
        </Form.Provider>
    );
};

export type { GeneralFormValues };
export { CampaignGeneralSettings };
